import { SDK_LOAD_FAILED } from './constants';
import { providerError } from './providerError';

/**
 * Loads a provider SDK one time. It resolves when the SDK is ready. It rejects
 * when the browser cannot get the file.
 *
 * All callers share one promise for each source. A check for an existing
 * `<script>` tag resolves while that tag still loads. The caller then reads
 * `window.AppleID` too early and reports the provider as unavailable.
 *
 * A failed load leaves the map, so a later attempt starts again.
 */
const loading = new Map<string, Promise<void>>();

const loadSdk = (source: string) => {
  const inFlight = loading.get(source);

  if (inFlight !== undefined) {
    return inFlight;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = source;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      script.remove();
      loading.delete(source);
      reject(providerError(SDK_LOAD_FAILED, `Failed to load ${source}`));
    });

    document.body.append(script);
  });

  loading.set(source, promise);

  return promise;
};

export { loadSdk };
